import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ErrorOutline as ErrorOutlineIcon } from '@mui/icons-material';
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Typography,
} from '@mui/material';
import { sso } from './sso';

// Friendly, retryable copy per failure reason — raw vendor text never surfaces.
const ERROR_COPY = {
  provider_error: 'The sign-in provider could not complete your request.',
  invalid_state:
    'This sign-in link is no longer valid. Please start signing in again.',
  missing_code: 'The sign-in response was incomplete.',
  exchange_failed:
    "We couldn't finish signing you in. This is usually temporary.",
};

/**
 * OAuth redirect target: `${origin}/oauth/callback`.
 *
 * A bare transit page — deliberately NOT behind RequireAuth (it must run before any
 * session exists) and it reads the auth instance directly rather than through session
 * context. All sequencing lives in `handleOAuthCallback`; this file only drives the
 * three visible states: working, navigate away on success, or a retryable error.
 */
export default function OAuthCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const [retrying, setRetrying] = useState(false);
  // The authorization code is single-use — run the exchange exactly once, even under
  // React StrictMode's double-invoke.
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    const search = {
      code: searchParams.get('code') ?? undefined,
      state: searchParams.get('state') ?? undefined,
      error: searchParams.get('error') ?? undefined,
      error_description: searchParams.get('error_description') ?? undefined,
    };
    sso
      .handleOAuthCallback(search)
      .then((outcome) => {
        if (outcome.status === 'success') {
          // destination is the pre-login location captured through the OIDC state
          // round-trip. Reduce a full URL to a same-origin path so we never open-redirect
          // off-origin; react-router navigate() takes the full path+query+hash string.
          let dest = '/';
          try {
            const u = new URL(
              outcome.destination || '/',
              window.location.origin
            );
            if (u.origin === window.location.origin) {
              dest = `${u.pathname}${u.search}${u.hash}`;
            }
          } catch {
            /* already a path */
          }
          navigate(dest, { replace: true });
        } else {
          setError(outcome.reason || 'exchange_failed');
        }
      })
      .catch(() => setError('exchange_failed'));
  }, [searchParams, navigate]);

  async function restartSignIn() {
    setRetrying(true);
    // Retry = restart the authorize leg (fresh verifier/state) — never re-POST a code.
    try {
      const url = await sso.beginLogin('/');
      window.location.assign(url);
    } catch {
      window.location.assign('/');
    }
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
      }}
    >
      {error === null ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            py: 6,
          }}
        >
          <CircularProgress />
          <Typography color="text.secondary">Completing sign-in…</Typography>
        </Box>
      ) : (
        <Paper
          elevation={0}
          variant="outlined"
          sx={{ maxWidth: 420, width: '100%' }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
              py: 6,
              px: 3,
              textAlign: 'center',
            }}
          >
            <ErrorOutlineIcon color="error" sx={{ fontSize: 40 }} />
            <div>
              <Typography sx={{ fontWeight: 600 }}>
                Sign-in could not be completed
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {ERROR_COPY[error] || ERROR_COPY.exchange_failed}
              </Typography>
            </div>
            <Button
              variant="contained"
              onClick={restartSignIn}
              disabled={retrying}
            >
              {retrying ? 'Starting…' : 'Try signing in again'}
            </Button>
          </Box>
        </Paper>
      )}
    </Box>
  );
}
